import {
  FoodItemTag,
  FoodTag,
  FoodTagCategory,
} from "client/jspPlatformExperiment";

/** Alphabetical order on the displayed label (`tag_name`). */
export const byTagName = (a: FoodTag, b: FoodTag): number =>
  (a.tag_name ?? "").localeCompare(b.tag_name ?? "");

/**
 * Comparator for a hand-ranked tag order. Keyed by tag ID — IDs are stable
 * while tag names can be edited. Tags not in the ranked list (new tags) fall
 * to the end, alphabetically.
 */
const byTagIdOrder =
  (order: number[]) =>
  (a: FoodTag, b: FoodTag): number => {
    const rankA = order.indexOf(a.id);
    const rankB = order.indexOf(b.id);
    if (rankA !== -1 && rankB !== -1) {
      return rankA - rankB;
    }
    if (rankA !== -1) {
      return -1;
    }
    if (rankB !== -1) {
      return 1;
    }
    return byTagName(a, b);
  };

/** Texture tags from normal to most modified. */
export const byTextureOrder = byTagIdOrder([
  63, // Normale (texture)
  21, // Tendre
  22, // Molle
  23, // Haché
  24, // Purée
]);

/** Liquid thickness tags from normal to thickest. */
export const byLiquidThicknessOrder = byTagIdOrder([
  30, // Normale - Éviter multiphase
  25, // Légèrement épais – B18 – niv. 2
  26, // Modérément épais – B10 – niv. 3
  90, // Très épais - B4 - niv. 4
]);

/** Diet tags from normal to most restrictive. */
export const byDietOrder = byTagIdOrder([
  64, // Normale (diète)
  20, // Végétarien
  18, // Sans salière
  87, // Hyposodique (2,3g)
  17, // Diabétique
  19, // Cardiaque
  89, // Réduit en potassium
  62, // Intolérance au lactose
  80, // Enrichi
  34, // Gastro
  39, // Liquide Claire
  88, // Nutrition entérale
]);

/**
 * Preference sub-categories, in display order. The tag edit section and the
 * tag board break the preference tags into one section per sub-category; a
 * preference tag matching none of them (e.g. seasoning, side) falls into a
 * trailing "Other" group.
 */
export const PREFERENCE_SUBCATEGORIES = [
  { category: FoodTagCategory.APPETIZER, label: "Appetizer" },
  { category: FoodTagCategory.PROTEIN, label: "Protein" },
  { category: FoodTagCategory.SAUCE, label: "Sauce" },
  { category: FoodTagCategory.STARCH, label: "Starch" },
  { category: FoodTagCategory.VEGETABLE, label: "Vegetable" },
  { category: FoodTagCategory.DESSERT, label: "Dessert" },
] as const;

/**
 * Index of the sub-category a preference tag belongs to (first match in
 * PREFERENCE_SUBCATEGORIES order), or PREFERENCE_SUBCATEGORIES.length for
 * preference tags outside every sub-category ("Other").
 */
export const preferenceSubcategoryIndex = (
  tag: FoodTag | FoodItemTag,
): number => {
  const index = PREFERENCE_SUBCATEGORIES.findIndex((subcategory) =>
    tag.tag_categories.includes(subcategory.category),
  );
  return index === -1 ? PREFERENCE_SUBCATEGORIES.length : index;
};

const tagCategoriesRank = [
  "Diet Type",
  "Texture",
  "Liquid Thickness",
  "Preference",
];

export const getFoodTagCategoryDisplayName = (
  foodTag: Pick<FoodTag, "tag_categories">,
): string => {
  let catName = "[Unknown category]";
  if (foodTag.tag_categories.includes(FoodTagCategory.DIET_TYPE)) {
    catName = `Diet Type`;
  } else if (foodTag.tag_categories.includes(FoodTagCategory.FOOD_TEXTURE)) {
    catName = `Texture`;
  } else if (
    foodTag.tag_categories.includes(FoodTagCategory.LIQUID_THICKNESS)
  ) {
    catName = `Liquid Thickness`;
  } else if (foodTag.tag_categories.includes(FoodTagCategory.PREFERENCE)) {
    catName = `Preference`;
  }
  return catName;
};

/**
 * Sort tags so autocomplete groups appear in the fixed category order
 * (diet, texture, thickness, preference) — same ordering the recipe list
 * toolbar uses.
 */
export const sortFoodTagsByCategoryRank = <
  T extends Pick<FoodTag, "tag_categories">,
>(
  tags: T[],
): T[] =>
  [...tags].sort(
    (a, b) =>
      tagCategoriesRank.findIndex(
        (rank) => rank === getFoodTagCategoryDisplayName(a),
      ) -
      tagCategoriesRank.findIndex(
        (rank) => rank === getFoodTagCategoryDisplayName(b),
      ),
  );

/**
 * The tags currently in effect on a food: the backend keeps user-removed
 * tags on the food item (`removed_by_user` set), so any "does this food
 * have tag X" check must go through here.
 */
export const activeFoodItemTagIds = (
  tags: FoodItemTag[] | null | undefined,
): Set<number> =>
  new Set(
    (tags ?? []).filter((tag) => !tag.removed_by_user).map((tag) => tag.id),
  );
